import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function DeleteAccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // If they're not logged in, send them to login so they can't land here cold.
  if (!user) {
    return (
      <div className="container-app py-14 max-w-xl text-center">
        <p className="text-stone-600 mb-4">Please sign in to delete your account.</p>
        <Link to="/login" className="text-orange-600 hover:text-orange-700 underline">
          Go to sign in
        </Link>
      </div>
    );
  }

  const isAdmin = user.role === 'admin';
  const canSubmit = confirmText === 'DELETE' && !deleting && !isAdmin;

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setDeleting(true);
    try {
      const { data } = await api.delete('/auth/me', {
        data: { confirm: 'DELETE' },
      });
      // Backend returns mode: "deleted" | "anonymized". We log out either way.
      const msg =
        data?.mode === 'anonymized'
          ? 'Your account has been anonymised. Your historical pledges and activity remain on the team\u2019s history but no longer identify you.'
          : 'Your account and all associated data have been deleted.';
      toast.success(msg);
      // Burn local token + redirect home.
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  return (
    <div className="container-app py-10 md:py-14 max-w-2xl" data-testid="delete-account-page">
      <Link
        to="/profile"
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-orange-600 mb-6"
        data-testid="delete-account-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Profile
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-5 h-5 text-red-600" />
        <span className="text-red-600 font-medium text-xs tracking-wider uppercase">Danger zone</span>
      </div>
      <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">
        Delete your account
      </h1>
      <p className="text-stone-500 mb-8">
        This action cannot be undone. Please read the details below before continuing.
      </p>

      <Card className="bg-white rounded-2xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] mb-6">
        <CardContent className="p-6 space-y-4 text-sm text-stone-700 leading-relaxed">
          <div>
            <p className="font-bold text-stone-900 mb-1">What gets deleted</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Your sign-in credentials (you will not be able to log in again)</li>
              <li>Your profile (name, display name, photo, country, contact info)</li>
              <li>Any pending invites you sent or received</li>
              <li>Password-reset tokens</li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-stone-900 mb-1">What is preserved (anonymised)</p>
            <p className="mb-2">
              If you have any donations or pledges on the platform, your historical{' '}
              <strong>activity entries</strong>, <strong>pledges you made</strong>, and{' '}
              <strong>pledges supporters made to you</strong> are kept on the team&rsquo;s
              history so your supporters&rsquo; records stay intact. They will appear under{' '}
              <em>&ldquo;[Deleted Walker]&rdquo;</em> and can no longer be linked back to you.
            </p>
            <p className="text-stone-500">
              If you have no financial history on the platform, your account is hard-deleted with no
              remaining trace.
            </p>
          </div>

          <div className="pt-2 border-t border-stone-100">
            <p className="text-xs text-stone-500">
              Account: <span className="font-mono text-stone-700">{user.email}</span>
              {user.display_name ? <span className="text-stone-500"> &middot; {user.display_name}</span> : null}
            </p>
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
          Admin accounts cannot self-delete from this page. Please contact another admin or KEF
          support if you need an admin account removed.
        </div>
      )}

      <form onSubmit={handleDelete} className="space-y-4">
        <label htmlFor="confirm-input" className="block text-sm font-medium text-stone-900">
          To confirm, type <span className="font-mono font-bold text-red-600">DELETE</span> below:
        </label>
        <Input
          id="confirm-input"
          type="text"
          autoComplete="off"
          spellCheck={false}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="DELETE"
          disabled={isAdmin || deleting}
          className="rounded-xl font-mono"
          data-testid="delete-account-confirm-input"
        />
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            type="submit"
            disabled={!canSubmit}
            className="rounded-full bg-red-600 hover:bg-red-700 disabled:bg-stone-300 disabled:text-stone-500 text-white font-medium px-6"
            data-testid="delete-account-submit-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {deleting ? 'Deleting...' : 'Permanently delete my account'}
          </Button>
          <Link
            to="/profile"
            className="inline-flex items-center justify-center rounded-full border border-stone-200 hover:bg-stone-50 text-stone-700 font-medium px-6 h-10 text-sm"
            data-testid="delete-account-cancel-link"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
